export function MapControls({
  colorMode, setColorMode,
  visibleCategories, toggleCategory,
  multiThreshold, setMultiThreshold,
  pinSize, handlePinSizeChange,
  showZipBoundaries, setShowZipBoundaries,
  selectedYears, availableYears, toggleYear, setSelectedYears,
  compact = false,
}) {
  const yearFilterActive = selectedYears.length > 0;
  const rowPad = compact ? 'px-3 py-1' : 'px-4 py-2';
  const rowPadTight = compact ? 'px-3 py-0.5' : 'px-4 py-1.5';
  const yearsWrap = compact ? 'flex-nowrap overflow-x-auto' : 'flex-wrap';

  return (
    <div className={compact ? 'bg-card' : 'sticky top-[57px] z-[499] bg-card'}>
      {/* Color Mode Toggle + Legend */}
      <div className={`${rowPad} border-b flex flex-wrap items-center gap-3 text-xs`}>
        <div className="flex items-center bg-muted rounded-sm p-0.5 gap-0.5" data-testid="color-mode-toggle">
          <button
            onClick={() => setColorMode('activity')}
            className={`px-2.5 py-1 rounded-sm font-medium transition-colors ${colorMode === 'activity' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
          >Activity</button>
          <button
            onClick={() => setColorMode('party')}
            className={`px-2.5 py-1 rounded-sm font-medium transition-colors ${colorMode === 'party' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
          >Party</button>
        </div>
        <div className="h-4 w-px bg-border" />
        {colorMode === 'activity' ? (
          <>
            <button onClick={() => toggleCategory('single')} className={`flex items-center gap-1 px-1.5 py-0.5 rounded transition-opacity ${visibleCategories.single ? 'opacity-100' : 'opacity-30'}`}><div className="w-3 h-3 rounded-full bg-blue-500" /><span>Single Donor</span></button>
            <button onClick={() => toggleCategory('repeat')} className={`flex items-center gap-1 px-1.5 py-0.5 rounded transition-opacity ${visibleCategories.repeat ? 'opacity-100' : 'opacity-30'}`}><div className="w-3 h-3 rounded-full bg-violet-500" /><span>Repeat Donations</span></button>
            <button onClick={() => toggleCategory('multi23')} className={`flex items-center gap-1 px-1.5 py-0.5 rounded transition-opacity ${visibleCategories.multi23 ? 'opacity-100' : 'opacity-30'}`}><div className="w-3 h-3 rounded-full bg-amber-500" /><span>Multi-Donor (2-{multiThreshold - 1})</span></button>
            <button onClick={() => toggleCategory('multi4')} className={`flex items-center gap-1 px-1.5 py-0.5 rounded transition-opacity ${visibleCategories.multi4 ? 'opacity-100' : 'opacity-30'}`}><div className="w-3 h-3 rounded-full bg-red-500" /><span>Multi-Donor (</span></button>
            <input
              type="number"
              min="2"
              max="100"
              value={multiThreshold}
              onChange={(e) => setMultiThreshold(Math.max(2, parseInt(e.target.value) || 2))}
              className="w-10 h-5 text-xs text-center border rounded bg-background font-mono -ml-2"
              data-testid="multi-threshold-input"
              title="Minimum unique donors for high-activity (red) pins"
            />
            <span className="text-xs -ml-1.5">+)</span>
          </>
        ) : (
          <>
            <button onClick={() => toggleCategory('democrat')} className={`flex items-center gap-1 px-1.5 py-0.5 rounded transition-opacity ${visibleCategories.democrat ? 'opacity-100' : 'opacity-30'}`}><div className="w-3 h-3 rounded-full bg-blue-600" /><span>Democrat</span></button>
            <button onClick={() => toggleCategory('republican')} className={`flex items-center gap-1 px-1.5 py-0.5 rounded transition-opacity ${visibleCategories.republican ? 'opacity-100' : 'opacity-30'}`}><div className="w-3 h-3 rounded-full bg-red-600" /><span>Republican</span></button>
            <button onClick={() => toggleCategory('mixed')} className={`flex items-center gap-1 px-1.5 py-0.5 rounded transition-opacity ${visibleCategories.mixed ? 'opacity-100' : 'opacity-30'}`}><div className="w-3 h-3 rounded-full bg-violet-600" /><span>Mixed</span></button>
            <button onClick={() => toggleCategory('unknown')} className={`flex items-center gap-1 px-1.5 py-0.5 rounded transition-opacity ${visibleCategories.unknown ? 'opacity-100' : 'opacity-30'}`}><div className="w-3 h-3 rounded-full bg-gray-400" /><span>Unknown</span></button>
          </>
        )}
      </div>

      {/* Pin Size Slider + ZIP Boundary Toggle + Year Filter */}
      <div className={`${rowPadTight} border-b flex items-center gap-3 text-xs`}>
        <span className="text-muted-foreground whitespace-nowrap">Pin Size</span>
        <input
          type="range" min="2" max="20" step="1" value={pinSize}
          onChange={(e) => handlePinSizeChange(Number(e.target.value))}
          className="flex-1 h-1.5 accent-primary cursor-pointer"
          data-testid="pin-size-slider"
        />
        <span className="text-muted-foreground w-6 text-right font-mono">{pinSize}</span>
        <div className="h-4 w-px bg-border" />
        <label className="flex items-center gap-1.5 cursor-pointer select-none whitespace-nowrap" data-testid="zip-boundary-toggle">
          <input
            type="checkbox"
            checked={showZipBoundaries}
            onChange={(e) => setShowZipBoundaries(e.target.checked)}
            className="h-3 w-3 rounded accent-primary"
          />
          ZIP Boundaries
        </label>
      </div>

      {/* Year Filter */}
      {availableYears.length > 0 && (
        <div className={`${rowPadTight} border-b flex items-center gap-2 text-xs`} data-testid="year-filter-bar">
          <span className="text-muted-foreground whitespace-nowrap font-medium">Years</span>
          <div className={`flex items-center gap-1 ${yearsWrap} min-w-0`}>
            <button
              onClick={() => setSelectedYears([])}
              className={`px-2 py-0.5 rounded-full border font-medium transition-colors ${
                !yearFilterActive
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background text-muted-foreground border-border hover:border-foreground/30'
              }`}
              data-testid="year-filter-all"
            >
              All
            </button>
            {availableYears.map(year => {
              const isSelected = selectedYears.includes(year);
              return (
                <button
                  key={year}
                  onClick={() => toggleYear(year)}
                  className={`px-2 py-0.5 rounded-full border font-mono font-medium transition-colors ${
                    isSelected
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background text-muted-foreground border-border hover:border-foreground/30'
                  }`}
                  data-testid={`year-filter-${year}`}
                >
                  {year}
                </button>
              );
            })}
          </div>
          {yearFilterActive && (
            <span className="text-amber-600 font-medium whitespace-nowrap ml-1">
              ({selectedYears.length} selected)
            </span>
          )}
        </div>
      )}
    </div>
  );
}
